<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { ActionButton, Card, Dialog, ErrorState, PageShell, StatusTag, TabStrip } from '@taler/ui'
import { formatDate, formatRate } from '@taler/domain'
import DetailNotFound from '@/app/components/DetailNotFound.vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
  canViewLoans,
  canDeleteLoan,
  drawnState,
  LoanAccrualProjections,
  LoanDrawdownForm,
  LoanPaymentForm,
  LoanPaymentHistory,
  LoanSummary,
  LoanSummaryIcon,
  LoanTermsDialog,
  LoanTypeIcon,
  type UpdateLoanTermsInput,
  useLoan,
  useLoanTerms,
} from '@/features/loans'
import { useSession } from '@/app/session'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const session = useSession()

const loanId = computed(() => route.params['loanId'] as string)
const canManage = computed(() => canViewLoans(session.can))
const projectionMonths = ref(12)
const editingTerms = ref(false)
const confirmingDelete = ref(false)

const loan = useLoan({
  ledgerId: () => session.activeLedger.value?.id,
  loanId: () => loanId.value,
  // The route itself already requires RECORDS_MANAGE (routes.ts); viewing is not gated a
  // second time here, the same way ContactDetail's view query is unconditional.
  enabled: () => true,
  projectionMonths: () => projectionMonths.value,
  locale: () => locale.value as 'en' | 'hu',
})

const terms = useLoanTerms({ ledgerId: () => session.activeLedger.value?.id })

const termsErrorMessage = computed(() =>
  terms.hasError.value ? (terms.errorDetail.value ?? t('loans.termsErrorBody')) : undefined,
)
const deleteErrorMessage = computed(() =>
  loan.hasDeleteError.value ? (loan.deleteErrorDetail.value ?? t('loans.deleteErrorBody')) : undefined,
)
const canDelete = computed(() =>
  loan.loan.value === undefined ? false : canDeleteLoan(loan.loan.value, loan.hasHistory.value),
)

function saveTerms(input: UpdateLoanTermsInput): void {
  if (loan.loan.value === undefined) return
  terms
    .update(loan.loan.value.id, input)
    .then(() => {
      editingTerms.value = false
    })
    .catch(() => {
      // surfaced through terms.hasError
    })
}

const paymentErrorMessage = computed(() =>
  loan.hasPaymentError.value ? (loan.paymentErrorDetail.value ?? t('loans.paymentErrorBody')) : undefined,
)

const drawdownErrorMessage = computed(() =>
  loan.hasDrawdownError.value ? (loan.drawdownErrorDetail.value ?? t('loans.drawdownErrorBody')) : undefined,
)

function recordDrawdown(input: Parameters<typeof loan.recordDrawdown>[0]): void {
  loan.recordDrawdown(input).catch(() => {
    // surfaced through loan.hasDrawdownError
  })
}

function recordPayment(input: Parameters<typeof loan.recordPayment>[0]): void {
  loan.recordPayment(input).catch(() => {
    // surfaced through loan.hasPaymentError; nothing further to do here
  })
}

async function remove(): Promise<void> {
  try {
    await loan.deleteLoan()
    await router.push({ name: 'loans' })
  } catch {
    // The mutation exposes its server reason next to the confirmation action.
  }
}

// Local tab state - the loan's own tabs are not routes, so TabStrip runs in its `select`-event
// mode (no `to`) instead of RouterLink navigation.
const TABS = ['overview', 'payments', 'interest', 'details'] as const
type LoanTab = (typeof TABS)[number]
const activeTab = ref<LoanTab>('overview')
const tabItems = computed(() =>
  TABS.map((tab) => ({ label: t(`loans.tabs.${tab}`), active: activeTab.value === tab })),
)
function selectTab(index: number): void {
  const tab = TABS[index]
  if (tab !== undefined) activeTab.value = tab
}

const loc = computed(() => locale.value as 'en' | 'hu')
const drawn = computed(() =>
  loan.loan.value ? drawnState(loan.loan.value, loan.hasHistory.value) : 'unknown',
)
const rateText = computed(() =>
  loan.loan.value ? `${formatRate(loan.loan.value.annualInterestRate, { locale: loc.value })}%` : '',
)

const statusBadge = computed(() => {
  const current = loan.loan.value
  if (current === undefined) return undefined
  if (current.overdue) return { tone: 'danger' as const, label: t('loans.overdue') }
  if (drawn.value === 'not-drawn') return { tone: 'neutral' as const, label: t('loans.notYetDrawn') }
  return { tone: 'ok' as const, label: t('loans.active') }
})

function goToPayments(): void {
  activeTab.value = 'payments'
}
</script>

<template>
  <PageShell class="gap-6">
    <p v-if="session.isLoading.value || loan.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <DetailNotFound v-else-if="loan.notFound.value" :to="{ name: 'loans' }" :list="t('loans.title')" />

    <ErrorState
      v-else-if="loan.error.value"
      :title="t('loans.detailErrorTitle')"
      :body="t('loans.errorBody')"
      :retry-label="t('loans.retry')"
      :retrying="loan.retrying.value"
      @retry="loan.retry()"
    />

    <template v-else-if="loan.loan.value">
      <RouterLink
        :to="{ name: 'loans' }"
        class="w-fit text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
      >
        ← {{ t('loans.title') }}
      </RouterLink>

      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-[var(--space-cell)]">
          <span
            aria-hidden="true"
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[color:color-mix(in_srgb,var(--color-accent)_12%,transparent)] text-[color:var(--color-accent)]"
          >
            <LoanTypeIcon :loan-type="loan.loan.value.loanType" />
          </span>
          <div>
            <div class="flex items-center gap-[var(--space-cell)]">
              <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
                {{ loan.loan.value.name }}
              </h1>
              <StatusTag v-if="statusBadge" :tone="statusBadge.tone">{{ statusBadge.label }}</StatusTag>
            </div>
            <p class="text-[color:var(--color-text-muted)]">
              <MoneyAmount :value="loan.loan.value.originalPrincipal" /> {{ t('loans.initialAmountSuffix') }}
            </p>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <ActionButton v-if="canManage" variant="secondary" type="button" @click="editingTerms = true">
            {{ t('loans.editTerms') }}
          </ActionButton>
          <ActionButton
            v-if="canManage && canDelete"
            variant="secondary"
            type="button"
            @click="confirmingDelete = true"
          >
            {{ t('loans.deleteAction') }}
          </ActionButton>
        </div>
      </div>

      <TabStrip :tabs="tabItems" :ariaLabel="t('loans.title')" as="button" @select="selectTab" />

      <template v-if="activeTab === 'overview'">
        <dl class="grid grid-cols-2 gap-[var(--space-cell)] lg:grid-cols-4">
          <Card class="flex min-w-0 flex-col gap-[var(--space-cell)]">
            <div class="flex items-center gap-[var(--space-cell)]">
              <span
                aria-hidden="true"
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[color:color-mix(in_srgb,var(--color-accent)_12%,transparent)] text-[color:var(--color-accent)]"
              >
                <LoanSummaryIcon kind="outstanding" />
              </span>
              <dt class="truncate text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                {{ t('loans.remainingBalance') }}
              </dt>
            </div>
            <dd class="truncate text-[color:var(--color-text)]">
              <span
                v-if="drawn === 'not-drawn'"
                class="text-[length:var(--text-step-3)] text-[color:var(--color-text-muted)]"
              >
                {{ t('loans.notYetDrawn') }}
              </span>
              <MoneyAmount v-else :value="loan.loan.value.outstandingPrincipal" size="hero" />
            </dd>
          </Card>
          <Card class="flex min-w-0 flex-col gap-[var(--space-cell)]">
            <div class="flex items-center gap-[var(--space-cell)]">
              <span
                aria-hidden="true"
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[color:color-mix(in_srgb,var(--color-accent)_12%,transparent)] text-[color:var(--color-accent)]"
              >
                <LoanSummaryIcon kind="monthly" />
              </span>
              <dt class="truncate text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                {{ t('loans.monthlyPayment') }}
              </dt>
            </div>
            <dd class="truncate text-[color:var(--color-text)]">
              <span
                v-if="loan.loan.value.monthlyPayment === undefined"
                class="text-[length:var(--text-step-3)] text-[color:var(--color-text-muted)]"
              >
                {{ t('loans.notSet') }}
              </span>
              <MoneyAmount v-else :value="loan.loan.value.monthlyPayment" size="hero" />
            </dd>
          </Card>
          <Card class="flex min-w-0 flex-col gap-[var(--space-cell)]">
            <div class="flex items-center gap-[var(--space-cell)]">
              <span
                aria-hidden="true"
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[color:color-mix(in_srgb,var(--color-accent)_12%,transparent)] text-[color:var(--color-accent)]"
              >
                <LoanSummaryIcon kind="interest" />
              </span>
              <dt class="truncate text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                {{ t('loans.annualInterestRate') }}
              </dt>
            </div>
            <dd
              class="truncate text-[length:var(--text-step-7)] font-semibold tabular-nums text-[color:var(--color-text)]"
            >
              {{ rateText }}
            </dd>
          </Card>
          <Card class="flex min-w-0 flex-col gap-[var(--space-cell)]">
            <div class="flex items-center gap-[var(--space-cell)]">
              <span
                aria-hidden="true"
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[color:color-mix(in_srgb,var(--color-accent)_12%,transparent)] text-[color:var(--color-accent)]"
              >
                <LoanSummaryIcon kind="monthly" />
              </span>
              <dt class="truncate text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                {{ t('loans.endDate') }}
              </dt>
            </div>
            <dd
              class="truncate text-[length:var(--text-step-7)] font-semibold tabular-nums text-[color:var(--color-text)]"
            >
              {{
                loan.loan.value.endDate === undefined
                  ? t('loans.notSet')
                  : formatDate(loan.loan.value.endDate, loc)
              }}
            </dd>
          </Card>
        </dl>

        <div class="grid grid-cols-1 gap-[var(--space-cell)] lg:grid-cols-[2fr_1fr]">
          <LoanAccrualProjections
            :projections="loan.projections.value"
            :months="projectionMonths"
            @update:months="projectionMonths = $event"
          />

          <Card class="min-w-0">
            <dl class="flex flex-col gap-[var(--space-cell)]">
              <h3 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
                {{ t('loans.loanDetails') }}
              </h3>
              <div class="flex items-center justify-between gap-[var(--space-cell)]">
                <dt class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                  {{ t('loans.originalPrincipal') }}
                </dt>
                <dd class="tabular-nums text-[color:var(--color-text)]">
                  <MoneyAmount :value="loan.loan.value.originalPrincipal" />
                </dd>
              </div>
              <div class="flex items-center justify-between gap-[var(--space-cell)]">
                <dt class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                  {{ t('loans.outstandingPrincipal') }}
                </dt>
                <dd class="tabular-nums text-[color:var(--color-text)]">
                  <span v-if="drawn === 'not-drawn'" class="text-[color:var(--color-text-muted)]">
                    {{ t('loans.notYetDrawn') }}
                  </span>
                  <MoneyAmount v-else :value="loan.loan.value.outstandingPrincipal" />
                </dd>
              </div>
              <div class="flex items-center justify-between gap-[var(--space-cell)]">
                <dt class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                  {{ t('loans.annualInterestRate') }}
                </dt>
                <dd class="tabular-nums text-[color:var(--color-text)]">{{ rateText }}</dd>
              </div>
              <div class="flex items-center justify-between gap-[var(--space-cell)]">
                <dt class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                  {{ t('loans.monthlyPayment') }}
                </dt>
                <dd class="tabular-nums text-[color:var(--color-text)]">
                  <span
                    v-if="loan.loan.value.monthlyPayment === undefined"
                    class="text-[color:var(--color-text-muted)]"
                  >
                    {{ t('loans.notSet') }}
                  </span>
                  <MoneyAmount v-else :value="loan.loan.value.monthlyPayment" />
                </dd>
              </div>
              <div class="flex items-center justify-between gap-[var(--space-cell)]">
                <dt class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                  {{ t('loans.startDate') }}
                </dt>
                <dd class="tabular-nums text-[color:var(--color-text)]">
                  {{ formatDate(loan.loan.value.drawdownDate, loc) }}
                </dd>
              </div>
              <div class="flex items-center justify-between gap-[var(--space-cell)]">
                <dt class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                  {{ t('loans.endDate') }}
                </dt>
                <dd class="tabular-nums text-[color:var(--color-text)]">
                  {{
                    loan.loan.value.endDate === undefined
                      ? t('loans.notSet')
                      : formatDate(loan.loan.value.endDate, loc)
                  }}
                </dd>
              </div>
              <div class="flex items-center justify-between gap-[var(--space-cell)]">
                <dt class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                  {{ t('loans.loanTypeLabel') }}
                </dt>
                <dd class="text-[color:var(--color-text)]">
                  {{
                    loan.loan.value.loanType === undefined
                      ? t('loans.notSet')
                      : t(`loans.loanType.${loan.loan.value.loanType}`)
                  }}
                </dd>
              </div>
              <div class="flex items-center justify-between gap-[var(--space-cell)]">
                <dt class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                  {{ t('loans.lender') }}
                </dt>
                <dd class="text-[color:var(--color-text)]">
                  {{ loan.loan.value.lender ?? t('loans.notSet') }}
                </dd>
              </div>
            </dl>
          </Card>
        </div>

        <section class="flex flex-col gap-[var(--space-cell)]">
          <div class="flex items-center justify-between gap-[var(--space-cell)]">
            <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
              {{ t('loans.paymentHistory') }}
            </h2>
            <button
              type="button"
              class="text-[length:var(--text-step-1)] text-[color:var(--color-accent)] underline"
              @click="goToPayments"
            >
              {{ t('loans.viewAll') }}
            </button>
          </div>
          <LoanPaymentHistory :payments="loan.payments.value" />
        </section>
      </template>

      <template v-else-if="activeTab === 'payments'">
        <section v-if="canManage" class="flex flex-col gap-[var(--space-cell)]">
          <LoanDrawdownForm
            :loan="loan.loan.value"
            :accounts="loan.drawdownAccounts.value"
            :is-submitting="loan.isRecordingDrawdown.value"
            :error-message="drawdownErrorMessage"
            @record="recordDrawdown"
          />
          <LoanPaymentForm
            :loan="loan.loan.value"
            :source-accounts="loan.sourceAccounts.value"
            :is-submitting="loan.isRecordingPayment.value"
            :error-message="paymentErrorMessage"
            @record="recordPayment"
          />
        </section>

        <section class="flex flex-col gap-[var(--space-cell)]">
          <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
            {{ t('loans.paymentHistory') }}
          </h2>
          <LoanPaymentHistory :payments="loan.payments.value" />
        </section>
      </template>

      <section v-else-if="activeTab === 'interest'" class="flex flex-col gap-[var(--space-cell)]">
        <Card>
          <dl class="grid grid-cols-2 gap-x-[var(--space-block)] gap-y-[var(--space-cell)]">
            <div>
              <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                {{ t('loans.annualInterestRate') }}
              </dt>
              <dd
                class="text-[length:var(--text-step-3)] font-semibold tabular-nums text-[color:var(--color-text)]"
              >
                {{ rateText }}
              </dd>
            </div>
            <div>
              <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                {{ t('loans.accruedInterest') }}
              </dt>
              <dd>
                <MoneyAmount :value="loan.loan.value.accruedInterest" />
              </dd>
            </div>
            <div>
              <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                {{ t('loans.nextAccrualDate') }}
              </dt>
              <dd class="text-[color:var(--color-text)]">
                {{ formatDate(loan.loan.value.nextAccrualDate, loc) }}
              </dd>
            </div>
            <div>
              <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                {{ t('loans.accrualCount') }}
              </dt>
              <dd class="tabular-nums text-[color:var(--color-text)]">{{ loan.loan.value.accrualCount }}</dd>
            </div>
          </dl>
        </Card>
      </section>

      <Card v-else>
        <LoanSummary :loan="loan.loan.value" :has-history="loan.hasHistory.value" />
      </Card>

      <LoanTermsDialog
        v-if="editingTerms"
        :loan="loan.loan.value"
        :is-submitting="terms.isSaving.value"
        :error-message="termsErrorMessage"
        @save="saveTerms"
        @close="editingTerms = false"
      />
      <Dialog :open="confirmingDelete" :label="t('loans.deleteTitle')" @close="confirmingDelete = false">
        <div class="flex w-96 flex-col gap-4 p-[var(--space-block)]">
          <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
            {{ t('loans.deleteTitle') }}
          </h2>
          <p class="text-[color:var(--color-text-muted)]">{{ t('loans.deleteConsequence') }}</p>
          <StatusTag v-if="deleteErrorMessage" tone="danger">{{ deleteErrorMessage }}</StatusTag>
          <div class="flex gap-2">
            <button
              type="button"
              :disabled="loan.isDeleting.value"
              class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] bg-[color:var(--color-danger)] px-3 py-2 text-[color:var(--color-text-inverse)] disabled:opacity-60"
              @click="remove"
            >
              {{ loan.isDeleting.value ? t('loans.deleting') : t('loans.deleteAction') }}
            </button>
            <ActionButton type="button" variant="secondary" @click="confirmingDelete = false">
              {{ t('loans.cancel') }}
            </ActionButton>
          </div>
        </div>
      </Dialog>
    </template>
  </PageShell>
</template>
